import { IconBrandWhatsapp } from '@tabler/icons-react';
import { OctagonAlert, TriangleAlert } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { salesWhatsappLink } from '@constants/contact';
import { BILLING_ENABLED } from '@constants/features';
import { pageUrl } from '@constants/url';
import { useLanguage } from '@contexts/LanguageContext';
import useSubscription from '@hooks/subscription/use-subscription';
import { deriveTrialStatus } from '@utils/trial-status';
import { Alert, AlertDescription } from '@/components/lumen/alert';
import { Button } from '@/components/lumen/button';

/**
 * Shell-level trial notice, mounted once above every dashboard page: a slim
 * status strip in the BillingStatusBanner family (warn / err alert).
 * Amber from three days before the trial cuts off, red on the last day and
 * once it has lapsed. Deliberately not dismissable. Hidden on the
 * subscription page, which carries the same message above the plan picker.
 */
export default function TrialBanner() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const { data, loading, error } = useSubscription();

  if (loading || error || !data) return null;
  const trial = deriveTrialStatus(data);
  if (!trial.isTrial || (!trial.ended && !trial.endsSoon)) return null;
  if (pathname.startsWith(pageUrl.subscription)) return null;

  const endsToday = !trial.ended && trial.daysLeft === 0;
  const urgent = trial.ended || endsToday;
  const Icon = trial.ended ? OctagonAlert : TriangleAlert;

  const lead = trial.ended
    ? t('subscription.trial.strip.ended')
    : endsToday
      ? t('subscription.trial.strip.today')
      : t('subscription.trial.strip.soon').replace('{n}', String(trial.daysLeft));
  const detail = trial.ended ? t('subscription.trial.strip.endedDetail') : t('subscription.trial.strip.soonDetail');

  return (
    <Alert variant={urgent ? 'err' : 'warn'} role="status" data-testid="trial-banner" className="mb-5">
      <Icon aria-hidden />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <p>
          <span className="font-semibold text-fg">{lead}</span> {detail}
        </p>
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Without the billing area /subscription redirects to the dashboard,
              so sales is the only real way forward and a plan link would mislead. */}
          {BILLING_ENABLED && (
            <Button asChild variant="ghost" size="xs">
              <Link to={pageUrl.subscription}>
                {trial.ended ? t('subscription.banner.trial.cta') : t('subscription.trial.running.cta')}
              </Link>
            </Button>
          )}
          <Button asChild size="xs">
            <a
              href={salesWhatsappLink(t('subscription.trial.contactSales.message'))}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconBrandWhatsapp aria-hidden />
              {t('subscription.trial.contactSales')}
            </a>
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
